import axios from 'axios';
import type {
    AmplifierConnectorReadinessOperationType,
    ConnectorReadinessEnsureResponse,
} from '@ampsec/platform-client';
import {
    AmplifierConnectorReadinessOperation,
    CoverageCountsResponse,
    CreateNotificationDto,
    DepartmentMetricsDto,
    FilterCriteria,
    ActionExecutionDto,
    FindingPatchDto,
    AmpSettingUpsertDto,
    ProviderDto,
    TenantDto,
    ConnectorUpsertDto,
    RawAmpSettingsMap,
    ConnectorStatus,
    FlowSpecFilter,
    FlowSpecUpsertDto,
    FindingSpecDto,
    CustomScoreValueDto,
    CustomActionDto,
    CustomActionUpsertDto,
    AgentDto,
    CustomScoreCohortDto,
    AgentInviteDto,
    CustomScoreCohortUpsertDto,
    FlowSpecDto,
    RuleSetCreateDto,
    RuleSetUpdateDto,
    CreateRuleDtoSchema,
    RuleUpsertDtoSchema,
    FindingsInsightsDto,
    TrainingDto,
    Page,
    SaasAiAgentDto,
} from '@ampsec/platform-client';
import { fetchAuthenticatedContentfulEntries } from './contentfulProxy';
import { getApi } from 'Src/Api';
import { store } from 'Rdx/store';
import { GridType } from 'Core-utils/types/types';
import { AIServiceModelName } from 'Core-utils/constants/constants';
import { normalizeJsonFieldForPostgres } from 'Core-utils/helpers/jsonHelpers';
import { encodeUtf8ToBase64Url, encodeHtmlForTransport } from 'Core-utils/helpers/transportEncoding';

axios.defaults.baseURL = process.env.PLATFORM_API_URL;

const SAFE_API_PATH_SEGMENT = /^[A-Za-z0-9._-]{1,128}$/;

const assertSafeApiPathSegment = (value: string, label: string): string => {
    const segment = value?.trim() ?? '';
    if (!segment || !SAFE_API_PATH_SEGMENT.test(segment)) {
        throw new Error(`Invalid ${label}`);
    }
    return segment;
};

const getAuthHeaders = () => {
    const token = store.getState().auth.token;
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const getPhishingApiBaseUrl = () => {
    const base = process.env.PLATFORM_API_URL || '';
    if (!base) {
        return '';
    }
    const normalized = base.replace(/\/$/, '');
    if (normalized.includes('/api/')) {
        return normalized;
    }
    const version = process.env.PLATFORM_API_VERSION || 'v1';
    return `${normalized}/api/${version}`;
};

const buildPhishingUrl = (path: string) => {
    const base = getPhishingApiBaseUrl();
    if (!base) {
        return path;
    }
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

export const getMetricsReportsData = async (inputKey: string, filter?: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.reports.getMetrics(inputKey, filter);
    return response && response.data[0]?.results;
};

export const getCoverageCounts = async (): Promise<CoverageCountsResponse | null> => {
    const api = await getApi();
    return api?.usersInsights.getCounts() ?? null;
};

export const getUserCoverageReport = async (uid: string) => {
    const api = await getApi();
    return api?.usersInsights.get(uid) ?? {};
};

export const getMetricsHealthScoreData = async (inputKey: string, filter?: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.reports.getMetrics(inputKey, filter);
    return response && response.data;
};

export const getMetricsTrendsReportsData = async (inputKey: string, filter?: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.reports.getMetricsTrend(inputKey, filter);
    return response && response.data;
};

export const getEnum = async (inputUrl: string, filter: FilterCriteria) => {
    const api = await getApi();
    const response = await (api?.enums as Record<string, any>)[inputUrl].list(filter);
    return response && response;
};

export const getUserDetails = async (userId: string) => {
    const api = await getApi();
    const response = await api?.users.getById(userId);
    return response && response.data[0];
};

export const getTenantsForCurrentUser = async (_inputUrl: string, filter?: FilterCriteria): Promise<TenantDto[]> => {
    const api = await getApi();
    const response = await api?.tenants.list(filter ?? {});
    return response?.data || [];
};

export const exchangeTokenForAnotherTenant = async (targetIenantId: string): Promise<string> => {
    const api = await getApi();
    const { token } = (await api?.identity.exchangeToken(targetIenantId)) || { token: '' };
    return token;
};

//TODO:Remove once all Live APIs are available
export const getApiData = async (inputUrl: string) => {
    axios.defaults.baseURL = process.env.REACT_MOCK_API_URL;
    axios.defaults.headers.common['Authorization'] = `Bearer ${process.env.REACT_MOCK_API_BEARER_TOKEN}`;

    const response = await axios.get(`/${inputUrl}`);
    return response && response.data.data;
};

export const getGridData = async (inputUrl: GridType, filter?: FilterCriteria, operation = 'list') => {
    const api = await getApi();
    const response = await (api as Record<string, any>)[inputUrl][operation](filter);
    if (response?.error) {
        throw response.error;
    }
    return response && response;
};

export const getEnums = async (inputUrlsJoin: string, filter: FilterCriteria) => {
    const inputUrls = inputUrlsJoin.split('__').filter((inputUrl) => inputUrl !== '');
    const promises = inputUrls.map((inputUrl) => getEnum(inputUrl, filter));
    const response = await Promise.allSettled(promises);
    return response;
};

export const getProvidersData = async (filter: FilterCriteria): Promise<ProviderDto[]> => {
    const api = await getApi();
    const response = await api?.providers.list(filter);
    return response?.data || [];
};

export const postEngageNotifications = async (postData: CreateNotificationDto[]) => {
    const api = await getApi();
    const response = await api?.notifications.createNew(postData);
    return response;
};

export const getInstalledConnectors = async (filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.connectors.list(filter);
    return response?.data;
};

export const getFindings = async (filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.findings.list(filter);
    return response && response.data[0];
};

export const getFindingsList = async (filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.findings.list(filter);
    return response?.data ? response.data : [];
};

export const patchFindingsStatus = async (findings: FindingPatchDto[]) => {
    const api = await getApi();
    const response = await api?.findings.patchAll(findings);
    return response;
};
export const createSetting = async (key: string, value: unknown, cid?: string) => {
    const api = await getApi();
    const response = await api?.settings.create(key, value, cid);
    return response;
};

export const updateSettings = async (id: string, entity: AmpSettingUpsertDto) => {
    const api = await getApi();
    const response = await api?.settings.update(id, entity);
    return response;
};

export const getTenantSettings = async (): Promise<RawAmpSettingsMap | undefined> => {
    const api = await getApi();
    const response = await api?.settings.getSettingsMap();
    return response?.getRawMap()?.tenantSettings;
};

export const getJamfConnectorSetting = async (cid: string): Promise<RawAmpSettingsMap | undefined> => {
    const api = await getApi();
    const response = await api?.settings.getSettingsMap();
    return response?.getRawMap()?.connectorSettings?.[cid];
};

export const installConnector = async (connectorModel: ConnectorUpsertDto) => {
    if (!connectorModel.pid) {
        throw new Error('Provider Id is required');
    }
    const api = await getApi();
    const response = await api?.connectors.create(connectorModel.pid, connectorModel.displayValue);
    return response?.id;
};

export const updateConnectorStatus = async (id: string, status: ConnectorStatus.ACTIVE | ConnectorStatus.DISABLED) => {
    const api = await getApi();
    const response = await api?.connectors.setStatus(id, status);
    return response;
};

export { AmplifierConnectorReadinessOperation };
export type { AmplifierConnectorReadinessOperationType, ConnectorReadinessEnsureResponse };

/** Idempotent Amplifier connector readiness via platform API (compliance vs AI Service Signals + rule). */
export const ensureAmplifierConnectorReadiness = async (
    operation: AmplifierConnectorReadinessOperationType,
): Promise<ConnectorReadinessEnsureResponse> => {
    const api = await getApi();
    if (!api) {
        throw new Error('Api not initialized');
    }
    return api.connectorReadiness.ensure(operation);
};

export const updateAllSettings = async (
    data: {
        id: string;
        value: unknown;
    }[],
) => {
    const api = await getApi();
    const response = await api?.settings.updateAll(data);
    return response;
};

export const getConnectorSettings = async () => {
    const api = await getApi();
    const response = await api?.settings.getSettingsMap();
    return response && response;
};

export const removeSetting = async (id: string) => {
    const api = await getApi();
    const response = await api?.connectors.delete(id);
    return response;
};

export const createPrediction = async (model: AIServiceModelName, question: string) => {
    const api = await getApi();
    const response = await api?.prediction.create({
        model: model,
        question: [question],
    });
    return response;
};

export const getFindingsView = async (
    filterCriteria: Record<string, unknown>,
    queryParams: FilterCriteria,
): Promise<Page<FindingsInsightsDto>> => {
    const api = await getApi();
    if (!api) {
        throw new Error('API is not available');
    }
    const response = await api.findingsInsights.create(filterCriteria, queryParams);
    if (response?.error) {
        throw new Error(response.error);
    }
    return response as Page<FindingsInsightsDto>;
};

export const getEngagementFlow = async (_inputKey: string, filterCriteria: FlowSpecFilter) => {
    const api = await getApi();
    const response = await api?.flowSpecs.getById(filterCriteria.id ?? '');
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const updateEngagementFlow = async (filterCriteria: FlowSpecUpsertDto) => {
    const api = await getApi();
    const payload: FlowSpecUpsertDto = {
        ...filterCriteria,
        filter:
            (normalizeJsonFieldForPostgres<FlowSpecDto['filter']>(filterCriteria.filter) as FlowSpecDto['filter']) ??
            filterCriteria.filter,
        triggerFilter:
            (normalizeJsonFieldForPostgres<FlowSpecDto['triggerFilter']>(
                filterCriteria.triggerFilter,
            ) as FlowSpecDto['triggerFilter']) ?? filterCriteria.triggerFilter,
    };
    const response = await api?.flowSpecs.update(payload);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const createFlow = async (flowSpec: FlowSpecUpsertDto) => {
    const api = await getApi();
    const response = await api?.flowSpecs.createFromName(flowSpec);
    return response;
};

export const deleteFlowById = async (id: string) => {
    const api = await getApi();
    const response = await api?.flowSpecs.delete(id);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const getCohorts = async (query?: string) => {
    const api = await getApi();
    const response = await api?.cohorts.list(query);
    return response?.data;
};

export const getFindingSpecs = async (inputKey: string, filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.findingSpecs.list(filter);
    return response?.data;
};

export const getFindingSpecsPage = async (inputKey: string, filter: FilterCriteria) => {
    const api = await getApi();
    return (await api?.findingSpecs.list(filter)) ?? { data: [], hints: { hasMore: false } };
};

export const updateFindings = async (model: FindingSpecDto) => {
    const api = await getApi();
    const response = await api?.findingSpecs.update(model);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const deleteFindingsById = async (id: string) => {
    const api = await getApi();
    const response = await api?.findingSpecs.delete(id);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const addFindingSpecs = async (model: FindingSpecDto) => {
    const api = await getApi();
    const response = await api?.findingSpecs.create(model);
    return response;
};

export const getFindingSpecsById = async (id: string): Promise<FindingSpecDto | undefined> => {
    const api = await getApi();
    const response = await api?.findingSpecs.getById(id);
    return response && response.data[0];
};

export const getHighRiskScore = async (_inputKey: string, query: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.customScoreValues.list(query);
    return response?.data;
};

export const createHighRiskScore = async (data: Omit<CustomScoreValueDto, 'createdAt' | 'updatedAt' | 'deletedAt'>) => {
    const api = await getApi();
    const response = await api?.customScoreValues.create(data);
    return response;
};

export const updateHighRiskScore = async (data: Omit<CustomScoreValueDto, 'createdAt' | 'updatedAt' | 'deletedAt'>) => {
    const api = await getApi();
    const response = await api?.customScoreValues.update(data);
    return response;
};

export const deleteHighRiskScore = async (id: string) => {
    const api = await getApi();
    const response = await api?.customScoreValues.delete(id);
    return response?.data;
};

export const getAllActions = async (inputKey: string, filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.customActions.list(filter);
    return response?.data;
};

export const addAction = async (model: CustomActionUpsertDto) => {
    const api = await getApi();
    const response = await api?.customActions.create(model);
    return response;
};

export const getActionById = async (id: string) => {
    const api = await getApi();
    const response = await api?.customActions.getById(id);
    return response && response?.data[0];
};

export const updateActionById = async (model: CustomActionDto) => {
    const api = await getApi();
    const response = await api?.customActions.update(model);
    return response;
};

export const deleteActionById = async (id: string) => {
    const api = await getApi();
    const response = await api?.customActions.delete(id);
    return response;
};

export const fetchServiceProfiles = async () => {
    const token = store.getState().auth.token;
    if (!token) {
        return [];
    }
    const response = await fetchAuthenticatedContentfulEntries(
        {
            ['content_type']: 'serviceProfile',
            include: 10,
            limit: 1000,
        },
        token,
    );
    return (response?.items ?? []).map((item: any) => {
        const fields = item?.fields ?? {};
        const logoUrl = fields?.logo?.fields?.file?.url;
        return {
            ...fields,
            logo: logoUrl ? `https:${logoUrl}` : '',
        };
    });
};

export const getActionsSummary = async (inputKey: string, filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.customActions.summary(filter);
    return response?.data;
};

export type CustomActionExecutionsPage = {
    data: ActionExecutionDto[];
    hints?: { count?: number; hasMore?: boolean; limit?: number; offset?: number };
};

export const getCustomActionExecutions = async (
    actionSpecId: string,
    filter?: FilterCriteria,
): Promise<CustomActionExecutionsPage> => {
    const safeActionSpecId = assertSafeApiPathSegment(actionSpecId, 'actionSpecId');
    const api = await getApi();
    if (!api?.customActions?.actionExecutions) {
        throw new Error('Custom action executions are not available in the platform client');
    }
    const page = await api.customActions.actionExecutions(safeActionSpecId, filter);
    return {
        data: Array.isArray(page?.data) ? page.data : [],
        hints: page?.hints,
    };
};

export const getHighRiskCohorts = async (_inputKey: string, query: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.customScoreCohorts.list(query);
    return response?.data;
};

export const createHighRiskCohorts = async (data: CustomScoreCohortUpsertDto) => {
    const api = await getApi();
    const response = await api?.customScoreCohorts.create(data);
    return response;
};

export const updateHighRiskCohorts = async (
    data: Omit<CustomScoreCohortDto, 'createdAt' | 'updatedAt' | 'deletedAt'>,
) => {
    const api = await getApi();
    const response = await api?.customScoreCohorts.update(data);
    return response;
};

export const deleteHighRiskCohort = async (value: string) => {
    const api = await getApi();
    const response = await api?.customScoreCohorts.delete(value);
    return response?.data;
};

export const getActionExecutions = async (inputKey: string, filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.actionExecutions.list(filter);
    return response?.data;
};
export const getAgentsList = async (inputUrl: string, filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.agents.list(filter);
    return response?.data;
};

export const inviteAgent = async (model: AgentInviteDto): Promise<AgentDto> => {
    const api = await getApi();
    const response = await api?.agents.invite(model);
    return response as unknown as AgentDto;
};

export const UpdateAgent = async (modal: AgentDto) => {
    const api = await getApi();
    const response = await api?.agents.update(modal);
    return response?.data;
};

export const deleteAgent = async (id: string) => {
    const api = await getApi();
    const response = await api?.agents.delete(id);
    return response?.data;
};

export const getWebhookURL = async (_inputKey: string, filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.tokens.list(filter);
    return response?.data;
};

export const triggerEngagement = async (
    fids: Array<string>,
    forceTrigger: boolean,
    allowDuplicates = true,
    overrideWorkingHours: boolean,
) => {
    const api = await getApi();
    const response = await api?.engagements.trigger({
        fids,
        allowDuplicates,
        overrideGhostMode: false,
        forceTrigger,
        overrideWorkingHours,
    });
    return response;
};

export const getAssetById = async (id: string) => {
    const api = await getApi();
    const response = await api?.asset.getById(id);
    return response && response.data[0];
};

export const getProfile = async (token: string) => {
    return await fetch(`${process.env.PLATFORM_API_URL}/me`, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

export const testEngagement = async (spec: FlowSpecDto, userIds: string[]) => {
    const api = await getApi();
    return api?.flowSpecs?.testEngagement(spec, { uids: userIds });
};

export const getUserCohorts = async (query?: string) => {
    const api = await getApi();
    const response = await api?.cohorts.listUserCohorts(query);
    return response?.data;
};

export const getEngagementLogs = async (filter: FilterCriteria) => {
    const api = await getApi();
    const response = await api?.engagementLogs.list(filter);
    return response;
};

export const getAnalyticsFindingMetrics = async (params?: {
    findingKinds?: string;
    periodType?: string;
    periodCount?: number;
}) => {
    const api = await getApi();
    const response = await api?.analytics.getFindingMetricsSeries(params);
    return response ?? [];
};

/** Response shape when includeDepartments is true. */
export interface AnalyticsFindingMetricsWithDepartmentsResponse {
    data: Awaited<ReturnType<typeof getAnalyticsFindingMetrics>>;
    departments?: DepartmentMetricsDto[];
}

export const getAnalyticsFindingMetricsWithDepartments = async (params?: {
    findingKinds?: string;
    periodType?: string;
    periodCount?: number;
}): Promise<AnalyticsFindingMetricsWithDepartmentsResponse> => {
    const api = await getApi();
    const response = await api?.analytics.getFindingMetricsSeriesWithDepartments(params);
    return {
        data: response?.data ?? [],
        ...(response?.departments?.length ? { departments: response.departments } : {}),
    };
};

/** Fetches analytics use cases (FlowSpec + category). Pass includeFlowSpecUseCases to include flow-based use cases. */
export const getAnalyticsUseCases = async (params?: { includeFlowSpecUseCases?: boolean }) => {
    const api = await getApi();
    return api?.analytics.getUseCases(params) ?? { flowSpecUseCases: [], categoryUseCases: [] };
};

export interface GuardedAnalyticsQueryMeta {
    tenantId: string;
    rowCount: number;
    returnedRowCount: number;
    queryHash: string;
    executionMs: number;
    limit: number;
    partial: boolean;
}

export interface GuardedAnalyticsQueryResponse {
    rows: Record<string, unknown>[];
    meta: GuardedAnalyticsQueryMeta;
}

export interface AmpyConverseResponse {
    answer: string;
    contextId: string;
    conversationId: string;
}

export interface AmpyAcknowledgeResponse {
    acknowledgement: string;
}

export interface AnalyticsChatContextMessage {
    id: string;
    sender: 'user' | 'ampy';
    message: string;
    timestamp: string;
}

export interface AnalyticsChatContextResponse {
    contextId: string;
    conversationId: string;
    messages: AnalyticsChatContextMessage[];
}

export const runAnalyticsGuardedQuery = async (payload: {
    sql: string;
    purpose?: string;
    limit?: number;
}): Promise<GuardedAnalyticsQueryResponse> => {
    const api = await getApi();
    const restClient = (
        api?.analytics as unknown as {
            rest?: {
                call: (req: {
                    url: string;
                    method: string;
                    data?: unknown;
                }) => Promise<{ data?: unknown; error?: unknown }>;
            };
        }
    )?.rest;

    if (!restClient || typeof restClient.call !== 'function') {
        throw new Error('Analytics API client is unavailable');
    }

    const response = await restClient.call({
        url: '/api/v1/analytics/guarded-query',
        method: 'POST',
        data: payload,
    });

    if (response?.error) {
        throw response.error;
    }

    return response?.data as GuardedAnalyticsQueryResponse;
};

export const runAmpyConversation = async (payload: {
    message: string;
    contextId?: string;
    currentPage?: string;
}): Promise<AmpyConverseResponse> => {
    const encodedPayload = {
        message: encodeUtf8ToBase64Url(payload.message),
        contextId: payload.contextId ? encodeUtf8ToBase64Url(payload.contextId) : undefined,
        currentPage: payload.currentPage ? encodeUtf8ToBase64Url(payload.currentPage) : undefined,
    };

    const api = await getApi();
    const restClient = (
        api?.analytics as unknown as {
            rest?: {
                call: (req: {
                    url: string;
                    method: string;
                    data?: unknown;
                }) => Promise<{ data?: unknown; error?: unknown }>;
            };
        }
    )?.rest;

    if (!restClient || typeof restClient.call !== 'function') {
        throw new Error('API client is unavailable');
    }

    const response = await restClient.call({
        // Remove transportEncoding to revert to plain-text payloads after WAF tuning is complete.
        url: '/api/v1/ampy_conversation/converse?transportEncoding=b64url',
        method: 'POST',
        data: encodedPayload,
    });

    if (response?.error) {
        throw response.error;
    }

    return response?.data as AmpyConverseResponse;
};

export const acknowledgeAmpyConversation = async (payload: {
    message: string;
    currentPage?: string;
}): Promise<AmpyAcknowledgeResponse> => {
    const encodedPayload = {
        message: encodeUtf8ToBase64Url(payload.message),
        currentPage: payload.currentPage ? encodeUtf8ToBase64Url(payload.currentPage) : undefined,
    };

    const api = await getApi();
    const restClient = (
        api?.analytics as unknown as {
            rest?: {
                call: (req: {
                    url: string;
                    method: string;
                    data?: unknown;
                }) => Promise<{ data?: unknown; error?: unknown }>;
            };
        }
    )?.rest;

    if (!restClient || typeof restClient.call !== 'function') {
        throw new Error('API client is unavailable');
    }

    const response = await restClient.call({
        // Remove transportEncoding to revert to plain-text payloads after WAF tuning is complete.
        url: '/api/v1/ampy_conversation/acknowledge?transportEncoding=b64url',
        method: 'POST',
        data: encodedPayload,
    });

    if (response?.error) {
        throw response.error;
    }

    return response?.data as AmpyAcknowledgeResponse;
};

export const getOrCreateAmpyChatContext = async (contextId?: string): Promise<AnalyticsChatContextResponse> => {
    const api = await getApi();
    const restClient = (
        api?.analytics as unknown as {
            rest?: {
                call: (req: {
                    url: string;
                    method: string;
                    data?: unknown;
                }) => Promise<{ data?: unknown; error?: unknown }>;
            };
        }
    )?.rest;

    if (!restClient || typeof restClient.call !== 'function') {
        throw new Error('API client is unavailable');
    }

    const response = await restClient.call({
        url: '/api/v1/ampy_conversation/chat',
        method: 'POST',
        data: contextId ? { contextId } : {},
    });

    if (response?.error) {
        throw response.error;
    }

    return response?.data as AnalyticsChatContextResponse;
};

export const getAmpyChatContext = async (contextId: string): Promise<AnalyticsChatContextResponse> => {
    const api = await getApi();
    const restClient = (
        api?.analytics as unknown as {
            rest?: {
                call: (req: {
                    url: string;
                    method: string;
                    data?: unknown;
                }) => Promise<{ data?: unknown; error?: unknown }>;
            };
        }
    )?.rest;

    if (!restClient || typeof restClient.call !== 'function') {
        throw new Error('API client is unavailable');
    }

    const response = await restClient.call({
        url: `/api/v1/ampy_conversation/chat/${encodeURIComponent(contextId)}`,
        method: 'GET',
    });

    if (response?.error) {
        throw response.error;
    }

    return response?.data as AnalyticsChatContextResponse;
};

export const getDetailedEngagementLog = async (flowStateId: string) => {
    const api = await getApi();
    const response = await api?.engagementLogs.getById(flowStateId);
    return response;
};

export const createTrainingCampaign = async (trainingModuleDto: TrainingDto) => {
    const api = await getApi();
    const response = await api?.trainings.createTraining(trainingModuleDto);
    return response;
};

export const listPhishingCampaigns = async () => {
    const response = await axios.get(buildPhishingUrl('/phishing_campaigns'), { headers: getAuthHeaders() });
    return response?.data ?? [];
};

export const createPhishingCampaign = async (payload: {
    name: string;
    subject?: string;
    body?: string;
    templateId?: string;
    trainingCampaign?: boolean;
    cohorts?: Array<{ kind: string; value: string; inclusive: boolean }>;
    userIds?: string[];
    closeActionId?: string;
    sendNow?: boolean;
    scheduledAt?: string;
    scheduledEndAt?: string;
}) => {
    const encoded = {
        ...payload,
        // Encode HTML body as base64 to avoid WAF / Cloud Armor OWASP CRS rules
        // blocking raw HTML in the request body. Decoded on read before rendering.
        ...(payload.body !== undefined && { body: encodeHtmlForTransport(payload.body) }),
    };
    const response = await axios.post(buildPhishingUrl('/phishing_campaigns'), encoded, { headers: getAuthHeaders() });
    return response;
};

export const sendPhishingCampaign = async (id: string) => {
    const response = await axios.post(
        buildPhishingUrl(`/phishing_campaigns/${id}/send`),
        {},
        { headers: getAuthHeaders() },
    );
    return response?.data;
};

export const getPhishingCampaignMetrics = async (id: string) => {
    const response = await axios.get(buildPhishingUrl(`/phishing_campaigns/${id}/metrics`), {
        headers: getAuthHeaders(),
    });
    return response?.data;
};

export const getPhishingCampaignRecipients = async (id: string) => {
    const response = await axios.get(buildPhishingUrl(`/phishing_campaigns/${id}/recipients`), {
        headers: getAuthHeaders(),
    });
    return response?.data;
};

export type SsoSetupLink = { link: string | null; expiresAt: string | null };
// `provisioned` is false when the tenant has no Descope tenant yet; the UI hides the SSO section in that case.
export type SsoSetupLinkStatus = SsoSetupLink & { provisioned: boolean };

// Tenant-admin SSO setup link (Descope). Endpoint is admin/owner-gated server-side.
export const fetchSsoSetupLink = async (): Promise<SsoSetupLinkStatus> => {
    const response = await axios.get('/api/v1/sso/setup-link', { headers: getAuthHeaders() });
    return response?.data ?? { provisioned: false, link: null, expiresAt: null };
};

export const generateSsoSetupLink = async (): Promise<SsoSetupLink> => {
    const response = await axios.post('/api/v1/sso/setup-link', {}, { headers: getAuthHeaders() });
    return response?.data ?? { link: null, expiresAt: null };
};

// ── Customer-facing MCP access tokens (APD-1818) ────────────────────────────
// Endpoints are admin/owner-gated server-side. The plaintext token is returned
// exactly once, by `issueMcpToken`; nothing else can retrieve it.
export type McpTokenScope = 'amp.analytics.read' | 'amp.insights.read' | 'amp.findings.read' | 'amp.directory.read';

export type McpTokenSummary = {
    id: string;
    tid: string;
    name: string;
    prefix: string;
    scopes: McpTokenScope[];
    createdBy: string;
    createdAt: string;
    expiresAt: string;
    lastUsedAt: string | null;
    revokedAt: string | null;
};

export type IssuedMcpToken = { token: string; summary: McpTokenSummary };

/**
 * Canonical URL of the customer-facing MCP endpoint, for the connection snippet
 * shown after a token is issued. Mirrors getPhishingApiBaseUrl so a deployment
 * that already includes /api/ in PLATFORM_API_URL is not double-prefixed.
 */
export const getMcpEndpointUrl = (): string => {
    const version = process.env.PLATFORM_API_VERSION || 'v1';
    const base = (process.env.PLATFORM_API_URL || '').replace(/\/$/, '');
    if (!base) {
        // Only reachable when the build has no API URL configured. An obvious
        // placeholder beats an empty string, which would render a snippet the
        // customer could copy and never notice was broken.
        return `https://<your-amplifier-host>/api/${version}/mcp/tenant`;
    }
    const apiRoot = base.includes('/api/') ? base : `${base}/api/${version}`;
    return `${apiRoot}/mcp/tenant`;
};

export const listMcpTokens = async (): Promise<McpTokenSummary[]> => {
    const response = await axios.get('/api/v1/mcp_tokens', { headers: getAuthHeaders() });
    return response?.data?.tokens ?? [];
};

export const issueMcpToken = async (input: {
    name: string;
    scopes: McpTokenScope[];
    expiresInDays?: number;
}): Promise<IssuedMcpToken> => {
    const response = await axios.post('/api/v1/mcp_tokens', input, { headers: getAuthHeaders() });
    return response?.data;
};

export const revokeMcpToken = async (tokenId: string): Promise<void> => {
    await axios.delete(`/api/v1/mcp_tokens/${encodeURIComponent(tokenId)}`, { headers: getAuthHeaders() });
};

export const getImpactSummary = async (flowSpecId: string, input: { temporalTypes?: Record<string, string[]> }) => {
    const api = await getApi();
    const response = await api?.flowSpecs.getImpactSummary(flowSpecId, input);
    return response;
};

export const terminateImpactedFlows = async (
    flowSpecId: string,
    input: { temporalTypes?: Record<string, string[]> },
) => {
    const api = await getApi();
    const response = await api?.flowSpecs.terminateImpactedWorkflows(flowSpecId, input);
    return response;
};

export const fetchRuleById = async (id: string) => {
    const api = await getApi();
    const response = await api?.rules.getRuleById(id);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const listRules = async (filter?: { tid?: string; cid?: string }) => {
    const api = await getApi();
    const response = await api?.rules.getRulesByTenant(filter?.tid ?? '', filter?.cid);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const createRule = async (payload: CreateRuleDtoSchema) => {
    const api = await getApi();
    const response = await api?.rules.createRuleSet(payload);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const updateRule = async (id: string, payload: RuleUpsertDtoSchema) => {
    const api = await getApi();
    const response = await api?.rules.updateRuleSet(id, payload);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const deleteRuleById = async (id: string) => {
    const api = await getApi();
    const response = await api?.rules.deleteRuleSet(id);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

/** @deprecated Prefer `fetchAllTagSpecsCatalog` / `useTagSpecsCatalog`. */
export const listTagSpecs = async (filter?: { tid?: string; includeGlobals?: boolean }) => {
    const { fetchAllTagSpecsCatalog } = await import('Apis/tagSpecsCatalog');
    const tid = filter?.tid;
    if (!tid) {
        return { data: [] };
    }
    const specs = await fetchAllTagSpecsCatalog(tid, filter?.includeGlobals ?? true);
    return { data: specs };
};

export const createTagSpec = async (payload: any) => {
    const api = await getApi();
    const response = await api?.tagSpecsService.create(payload);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const updateTagSpec = async (id: string, payload: any) => {
    const api = await getApi();
    const response = await api?.tagSpecsService.update(id, payload);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const deleteTagSpecById = async (id: string) => {
    const api = await getApi();
    const response = await api?.tagSpecsService.delete(id);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

// ─── Rule Sets ────────────────────────────────────────────────────────────────

export const listRuleSets = async (filter?: { tid?: string; limit?: number }) => {
    const api = await getApi();
    const params: Record<string, string | number> = { limit: filter?.limit ?? 200 };
    if (filter?.tid) {
        params.tid = filter.tid;
    }
    const response = await api?.ruleSets.findAll(params);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const createRuleSet = async (payload: RuleSetCreateDto) => {
    const api = await getApi();
    const response = await api?.ruleSets.create(payload);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const updateRuleSetById = async (id: string, payload: RuleSetUpdateDto) => {
    const api = await getApi();
    const response = await api?.ruleSets.update(id, payload);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const deleteRuleSetById = async (id: string) => {
    const api = await getApi();
    const response = await api?.ruleSets.delete(id);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

export const setRuleSetAsDefault = async (id: string) => {
    const api = await getApi();
    const response = await api?.ruleSets.setAsDefault(id);
    if (response?.error) {
        throw response.error;
    }
    return response;
};

/** Paginated read of reconciled saas AI agents for Agent Management (APD-1921). */
export const fetchAllSaasAiAgents = async (): Promise<SaasAiAgentDto[]> => {
    const api = await getApi();
    if (!api?.saasAiAgents) {
        return [];
    }

    const limit = 100;
    let offset = 0;
    let hasMore = true;
    const records: SaasAiAgentDto[] = [];

    while (hasMore) {
        const page: Page<SaasAiAgentDto> = await api.saasAiAgents.list({ limit, offset });
        records.push(...(page.data ?? []));
        hasMore = page.hints?.hasMore ?? false;
        offset += limit;
    }

    return records;
};

/** Fetch AI agent sessions for a specific agent (APD-1948). */
export const fetchSaasAiAgentSessions = async (filter: FilterCriteria) => {
    const response = await axios.get('/api/v1/saas_aiagent_sessions', {
        params: filter,
        headers: getAuthHeaders(),
    });
    return response?.data ?? { data: [], hints: {} };
};
